import { api } from "./backend/api.js";

/**
 * Fetch roster by project ID (cached by default; use refresh: true to fetch from DataBricks and update cache).
 * @param {string} projectId - Salesforce project ID
 * @param {{ refresh?: boolean }} [options] - Set refresh: true to trigger DataBricks refresh and update cache
 * @returns {Promise<{ data: Array, last_synced_at: string|null }>} data array and last_synced_at (ISO string or null)
 */
export async function fetchRosterByProject(projectId, options = {}) {
  if (!projectId) {
    throw new Error("projectId is required");
  }
  try {
    const url =
      options.refresh === true
        ? `/roster/project/${projectId}?refresh=true`
        : `/roster/project/${projectId}`;
    const response = await api.get(url);
    const data = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];
    const last_synced_at = response?.last_synced_at ?? null;
    return { data, last_synced_at };
  } catch (error) {
    console.error("Error fetching roster by project:", error);
    throw error;
  }
}

/**
 * Fetch roster by project objective ID
 * @param {string} projectObjectiveId - Salesforce project objective ID
 * @returns {Promise<Array>} Array of contributor project records
 */
export async function fetchRosterByProjectObjective(projectObjectiveId) {
  if (!projectObjectiveId) {
    throw new Error("projectObjectiveId is required");
  }
  try {
    const response = await api.get(
      `/roster/project-objective/${projectObjectiveId}`,
    );
    // Handle wrapped response format: { success: true, data: [...] }
    if (response && response.success && Array.isArray(response.data)) {
      return response.data;
    }
    // Handle direct array response or response.data
    return Array.isArray(response.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];
  } catch (error) {
    console.error("Error fetching roster by project objective:", error);
    throw error;
  }
}

/**
 * Fetch all contributor details by project ID (cached by default; use refresh: true to fetch from DataBricks).
 * Same backend as roster/project; returns data and last_synced_at.
 * @param {string} projectId - Salesforce project ID
 * @param {{ refresh?: boolean }} [options] - Set refresh: true to trigger DataBricks refresh and update cache
 * @returns {Promise<{ data: Array, last_synced_at: string|null }>} data array and last_synced_at (ISO string or null)
 */
export async function fetchContributorDetailsByProject(projectId, options = {}) {
  if (!projectId) {
    throw new Error("projectId is required");
  }
  try {
    const path = `/roster/contributors/by-project/${projectId}`;
    const url =
      options.refresh === true
        ? `${path}?refresh=true&_=${Date.now()}`
        : path;
    const response = await api.get(url);
    const data = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];
    const last_synced_at = response?.last_synced_at ?? null;
    return { data, last_synced_at };
  } catch (error) {
    console.error("Error fetching contributor details by project:", error);
    throw error;
  }
}

/**
 * Fetch all contributor details by project objective ID (cached by default; use refresh: true to fetch from DataBricks).
 * @param {string} projectObjectiveId - Salesforce project objective ID
 * @param {{ refresh?: boolean }} [options] - Set refresh: true to trigger DataBricks refresh and update cache
 * @returns {Promise<{ data: Array, last_synced_at: string|null }>} data array and last_synced_at (ISO string or null)
 */
export async function fetchContributorDetailsByProjectObjective(
  projectObjectiveId,
  options = {},
) {
  if (!projectObjectiveId) {
    throw new Error("projectObjectiveId is required");
  }
  try {
    const path = `/roster/contributors/by-project-objective/${projectObjectiveId}`;
    const url =
      options.refresh === true
        ? `${path}?refresh=true&_=${Date.now()}`
        : path;
    const response = await api.get(url);
    const data =
      response && response.success && Array.isArray(response.data)
        ? response.data
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
    const last_synced_at = response?.last_synced_at ?? null;
    return { data, last_synced_at };
  } catch (error) {
    console.error(
      "Error fetching contributor details by project objective:",
      error,
    );
    throw error;
  }
}

/**
 * Fetch roster counts by project ID. Optional refresh to load from DataBricks and update cache.
 * @param {string} projectId - Salesforce project ID
 * @param {{ refresh?: boolean }} [options]
 * @returns {Promise<Array>} Array of status count objects
 */
export async function fetchRosterCountsByProject(projectId, options = {}) {
  if (!projectId) {
    throw new Error("projectId is required");
  }
  try {
    const path = `/roster/project/${projectId}/counts`;
    const url = options.refresh === true ? `${path}?refresh=true&_=${Date.now()}` : path;
    const response = await api.get(url);
    const data = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];
    return data;
  } catch (error) {
    console.error("Error fetching roster counts by project:", error);
    throw error;
  }
}

/**
 * Transform counts array to summary object format
 * @param {Array} counts - Array of count objects with cp_status and cp_count
 * @returns {Object} Summary object with total_cps, active_count, production_count
 */
export function transformCountsToSummary(counts) {
  if (!Array.isArray(counts) || counts.length === 0) {
    return {
      total_cps: 0,
      active_count: 0,
      production_count: 0,
    };
  }

  let totalCps = 0;
  let activeCount = 0;
  let productionCount = 0;

  counts.forEach((item) => {
    const count = item.cp_count || 0;
    totalCps += count;

    if (item.cp_status === "Active") {
      activeCount = count;
    } else if (item.cp_status === "Production") {
      productionCount = count;
    }
  });

  return {
    total_cps: totalCps,
    active_count: activeCount,
    production_count: productionCount,
  };
}

/**
 * Fetch roster counts by project objective ID grouped by status
 * @param {string} projectObjectiveId - Salesforce project objective ID
 * @returns {Promise<Array>} Array of status count objects
 */
export async function fetchRosterCountsByProjectObjective(projectObjectiveId) {
  if (!projectObjectiveId) {
    throw new Error("projectObjectiveId is required");
  }
  try {
    const response = await api.get(
      `/roster/project-objective/${projectObjectiveId}/counts`,
    );
    return response.data || response;
  } catch (error) {
    console.error("Error fetching roster counts by project objective:", error);
    throw error;
  }
}

/**
 * Fetch roster summary by project objective ID (cached; optional refresh).
 * @param {string} projectObjectiveId - Salesforce project objective ID
 * @param {{ refresh?: boolean }} [options] - Set refresh: true to trigger DataBricks refresh
 * @returns {Promise<{ summary: Object, last_synced_at: string|null }>} summary object and last_synced_at
 */
export async function fetchRosterSummaryByProjectObjective(
  projectObjectiveId,
  options = {},
) {
  if (!projectObjectiveId) {
    throw new Error("projectObjectiveId is required");
  }
  try {
    const path = `/roster/project-objective/${projectObjectiveId}/summary`;
    const url =
      options.refresh === true
        ? `${path}?refresh=true&_=${Date.now()}`
        : path;
    const response = await api.get(url);
    const summary =
      response && response.success && response.data
        ? response.data
        : response?.data || response;
    const last_synced_at = response?.last_synced_at ?? null;
    return { summary, last_synced_at };
  } catch (error) {
    const msg = error?.message ?? String(error);
    if (msg.toLowerCase().includes("not found")) {
      return { summary: null, last_synced_at: null };
    }
    console.error("Error fetching roster summary by project objective:", error);
    throw error;
  }
}

/**
 * Fetch project objectives by project ID. Optional refresh to load from DataBricks and update cache.
 * @param {string} projectId - Salesforce project ID
 * @param {{ refresh?: boolean }} [options]
 * @returns {Promise<Array>} Array of project objective records
 */
export async function fetchProjectObjectivesByProject(projectId, options = {}) {
  if (!projectId) {
    throw new Error("projectId is required");
  }
  try {
    const path = `/roster/project/${projectId}/objectives`;
    const url = options.refresh === true ? `${path}?refresh=true&_=${Date.now()}` : path;
    const response = await api.get(url);
    const data = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];
    return data;
  } catch (error) {
    console.error("Error fetching project objectives by project:", error);
    throw error;
  }
}
